"use client";

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { sanitizeRichHtml } from "@/utils/sanitizeHtml";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ContentNode, SectionStatus } from "./contentNode";

/** Tight context-column density vs. roomy reader window. */
export type ReadableContentVariant = "panel" | "window";

export interface ReadableContentProps {
  node: ContentNode | null | undefined;
  className?: string;
  /** Layout density. ``window`` expands more sections by default. */
  variant?: ReadableContentVariant;
  /** Nesting depth for section indentation (internal). */
  depth?: number;
}

const STATUS_STYLES: Record<SectionStatus, string> = {
  ok: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/20",
  warn: "bg-amber-500/15 text-amber-300 ring-amber-500/20",
  error: "bg-red-500/15 text-red-300 ring-red-500/20",
};

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** Sections open by default down to this depth (inclusive) per variant. */
const AUTO_OPEN_DEPTH: Record<ReadableContentVariant, number> = {
  panel: -1,
  window: 1,
};

type ContentBadgeLabel = "JSON" | "HTML";

function hasNestedSection(node: ContentNode): boolean {
  return (node.blocks ?? []).some((b) => b.type === "section");
}

function sectionContentBadge(blocks: ContentNode[]): ContentBadgeLabel | null {
  if (blocks.length !== 1) return null;
  const only = blocks[0];
  if (only.type === "json") return "JSON";
  if (only.type === "html") return "HTML";
  return null;
}

function defaultSectionOpen(
  blocks: ContentNode[],
  nested: boolean,
  depth: number,
  variant: ReadableContentVariant
): boolean {
  if (sectionContentBadge(blocks)) return false;
  if (!nested) return true;
  return depth <= AUTO_OPEN_DEPTH[variant];
}

/** Clamp tall prose and reveal a "Show more" toggle when it overflows. */
function Spoiler({
  children,
  maxHeight = 220,
}: {
  children: ReactNode;
  maxHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollHeight > maxHeight + 8);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [maxHeight]);

  const clamped = overflowing && !expanded;

  return (
    <div>
      <div
        ref={ref}
        className={clsx(
          "overflow-hidden",
          clamped && "[mask-image:linear-gradient(to_bottom,black_70%,transparent)]"
        )}
        style={{ maxHeight: clamped ? maxHeight : undefined }}
      >
        {children}
      </div>
      {overflowing ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] font-medium text-indigo-300 hover:text-indigo-200"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
}

function CodePre({ children }: { children: ReactNode }) {
  return (
    <pre
      className={clsx(
        "overflow-x-auto whitespace-pre-wrap break-words rounded-md",
        "border border-white/10 bg-slate-900/70 p-2",
        "font-mono text-[11px] leading-relaxed text-slate-300"
      )}
    >
      {children}
    </pre>
  );
}

function JsonBody({ value }: { value: unknown }) {
  let display = "";
  try {
    display = JSON.stringify(value, null, 2);
  } catch {
    display = String(value);
  }
  return <CodePre>{display}</CodePre>;
}

function HtmlBody({ html }: { html: string }) {
  const [showSource, setShowSource] = useState(false);
  const sanitized = sanitizeRichHtml(html);

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setShowSource((v) => !v)}
        className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-300"
      >
        {showSource ? "Show rendered" : "Show source"}
      </button>
      {showSource ? (
        <CodePre>{html}</CodePre>
      ) : (
        <Spoiler maxHeight={220}>
          <div
            className="prose prose-sm max-w-none prose-invert text-xs text-slate-300"
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
        </Spoiler>
      )}
    </div>
  );
}

function MarkdownBlock({ text }: { text: string }) {
  return (
    <Spoiler maxHeight={220}>
      <div className="prose prose-sm max-w-none prose-invert text-xs text-slate-300">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </div>
    </Spoiler>
  );
}

function TextBlock({ text, className }: { text: string; className?: string }) {
  return (
    <Spoiler maxHeight={180}>
      <p
        className={clsx(
          "whitespace-pre-wrap break-words text-xs leading-relaxed text-slate-300",
          className
        )}
        data-testid="readable-content-text"
      >
        {text}
      </p>
    </Spoiler>
  );
}

function FieldsBlock({
  fields,
  variant,
}: {
  fields: { label: string; value: string }[];
  variant: ReadableContentVariant;
}) {
  const compact = variant === "panel";
  return (
    <dl className="space-y-1.5 text-xs">
      {fields.map((row, index) => (
        <div
          key={`${row.label}-${index}`}
          className="grid grid-cols-[minmax(0,42%)_minmax(0,1fr)] items-baseline gap-x-2"
        >
          <dt
            className={clsx(
              "font-medium text-slate-400",
              compact ? "truncate" : "break-words"
            )}
          >
            {row.label}
          </dt>
          <dd
            className={clsx(
              "min-w-0 text-slate-200",
              compact ? "truncate" : "whitespace-pre-wrap break-words"
            )}
            title={compact ? row.value : undefined}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function StatusChip({ status }: { status: SectionStatus }) {
  return (
    <span
      className={clsx(
        "shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ring-1 ring-inset",
        STATUS_STYLES[status]
      )}
    >
      {status}
    </span>
  );
}

function ContentBadge({ label }: { label: ContentBadgeLabel }) {
  return (
    <span
      className="shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400 ring-1 ring-inset ring-white/10"
      data-testid={`readable-content-badge-${label.toLowerCase()}`}
    >
      {label}
    </span>
  );
}

function sectionChildIndent(depth: number): string | false {
  return depth >= 1 ? "ml-1 border-l border-white/10 pl-2" : false;
}

function SectionBlock({
  node,
  variant,
  depth,
}: {
  node: ContentNode;
  variant: ReadableContentVariant;
  depth: number;
}) {
  const title = node.title ?? "Section";
  const blocks = node.blocks ?? [];
  const nested = hasNestedSection(node);
  const contentBadge = sectionContentBadge(blocks);
  const [open, setOpen] = useState(() =>
    defaultSectionOpen(blocks, nested, depth, variant)
  );

  return (
    <section className="min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="group flex w-full items-center gap-1.5 rounded py-0.5 text-left text-xs font-semibold text-slate-300 hover:text-white"
      >
        <span className="w-3 shrink-0 text-[10px] text-slate-500 group-hover:text-slate-300">
          {open ? "▾" : "▸"}
        </span>
        <span className="min-w-0 flex-1 truncate uppercase tracking-wide">{title}</span>
        {contentBadge ? <ContentBadge label={contentBadge} /> : null}
        {node.status ? <StatusChip status={node.status} /> : null}
      </button>
      {open ? (
        <div className={clsx("min-w-0 space-y-1.5 pb-0.5", sectionChildIndent(depth))}>
          {contentBadge === "JSON" && blocks[0]?.type === "json" ? (
            <JsonBody value={blocks[0].value} />
          ) : contentBadge === "HTML" && blocks[0]?.type === "html" ? (
            <HtmlBody html={blocks[0].html ?? ""} />
          ) : (
            blocks.map((block, index) => (
              <ReadableContent
                key={`${title}-${index}`}
                node={block}
                variant={variant}
                depth={depth + 1}
              />
            ))
          )}
        </div>
      ) : null}
    </section>
  );
}

/** Collapsible wrapper for leaf JSON/HTML nodes not already under a titled section. */
function PayloadLeaf({
  label,
  children,
  defaultOpen = false,
}: {
  label: ContentBadgeLabel;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="min-w-0" data-testid={`readable-content-payload-${label.toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="group flex w-full items-center gap-1.5 rounded py-0.5 text-left text-xs font-semibold text-slate-300 hover:text-white"
      >
        <span className="w-3 shrink-0 text-[10px] text-slate-500 group-hover:text-slate-300">
          {open ? "▾" : "▸"}
        </span>
        <ContentBadge label={label} />
      </button>
      {open ? <div className="mt-1 min-w-0">{children}</div> : null}
    </section>
  );
}

function rootEntityLabel(title: string | undefined): string {
  if (!title?.trim()) return "Entity";
  const segment = title.split(" · ")[0]?.trim();
  return segment || title.trim();
}

/** In panel preview, nest leading field rows under the entity name (matches window title). */
function panelRootBlocks(blocks: ContentNode[], entityLabel: string): ContentNode[] {
  const leadingFields: ContentNode[] = [];
  let index = 0;
  while (index < blocks.length && blocks[index].type === "fields") {
    leadingFields.push(blocks[index]);
    index += 1;
  }
  if (leadingFields.length === 0) return blocks;

  const mergedFields: ContentNode =
    leadingFields.length === 1
      ? leadingFields[0]
      : {
          type: "fields",
          fields: leadingFields.flatMap((block) => block.fields ?? []),
        };

  return [
    { type: "section", title: entityLabel, blocks: [mergedFields] },
    ...blocks.slice(index),
  ];
}

function renderRootSection(
  node: ContentNode,
  variant: ReadableContentVariant,
  className?: string
) {
  const rawBlocks = node.blocks ?? [];
  const blocks =
    variant === "panel"
      ? panelRootBlocks(rawBlocks, rootEntityLabel(node.title))
      : rawBlocks;
  return (
    <div className={clsx("min-w-0 space-y-1.5", className)} data-testid="readable-content">
      {variant === "window" && node.title ? (
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {node.title}
        </p>
      ) : null}
      {blocks.map((block, index) => (
        <ReadableContent
          key={`root-${index}`}
          node={block}
          variant={variant}
          depth={1}
        />
      ))}
    </div>
  );
}

/** Recursive renderer for structured audit content trees. */
export function ReadableContent({
  node,
  className,
  variant = "window",
  depth = 0,
}: ReadableContentProps) {
  if (!node) {
    return (
      <p className="text-sm text-slate-500" data-testid="readable-content-empty">
        No content.
      </p>
    );
  }

  if (depth === 0 && node.type === "section") {
    return renderRootSection(node, variant, className);
  }

  if (node.type === "section") {
    return (
      <div className={className} data-testid="readable-content-section">
        <SectionBlock node={node} variant={variant} depth={depth} />
      </div>
    );
  }

  if (node.type === "text") {
    return (
      <div className={className}>
        <TextBlock text={node.text ?? ""} />
      </div>
    );
  }

  if (node.type === "markdown") {
    return (
      <div className={className} data-testid="readable-content-markdown">
        <MarkdownBlock text={node.text ?? ""} />
      </div>
    );
  }

  if (node.type === "html") {
    return (
      <div className={className} data-testid="readable-content-html">
        <PayloadLeaf label="HTML">
          <HtmlBody html={node.html ?? ""} />
        </PayloadLeaf>
      </div>
    );
  }

  if (node.type === "json") {
    return (
      <div className={className} data-testid="readable-content-json">
        <PayloadLeaf label="JSON">
          <JsonBody value={node.value} />
        </PayloadLeaf>
      </div>
    );
  }

  if (node.type === "fields") {
    return (
      <div className={className} data-testid="readable-content-fields">
        <FieldsBlock fields={node.fields ?? []} variant={variant} />
      </div>
    );
  }

  return null;
}
